/**
 * Rahmen für alle Mandantenseiten: Kopfzeile mit Navigation, Mandantenwechsel,
 * Sprache und Abmelden. Der Mandant steht im Pfad (`/t/:tenantId`), damit zwei
 * Browser-Tabs nie unbemerkt auf verschiedenen Konten arbeiten; die API prüft ihn
 * zusätzlich bei jeder Anfrage. Gesperrte/geschlossene Konten mit `invoicesOnly` (nur Inhaber) zeigen ohne
 * Navigation ausschließlich die Rechnungsansicht unter `/t/:tenantId/account`.
 */
import { Alert, AppShell, Badge, Burger, Button, Center, Group, Loader, NavLink, Select, Stack, Text, type NavLinkProps } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createLink, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { forwardRef, lazy, Suspense, useEffect } from 'react';
import type { MembershipView } from '../../../shared/contract.js';
import { ApiRequestError } from '../api/client.js';
import { t } from '../texts.js';
import { lastTenant, rememberTenant, useServices } from '../services.js';

/** Mantine-NavLink als Router-Link (TanStack `createLink`). */
const RouterNavLink = createLink(
  forwardRef<HTMLAnchorElement, Omit<NavLinkProps, 'href'>>(function AnchorNavLink(props, ref) {
    return <NavLink component="a" ref={ref} {...props} />;
  }),
);

export function useMe() {
  const { api } = useServices();
  return useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 5 * 60 * 1000 });
}

export function FullPageLoader() {
  return (
    <Center mih="60vh">
      <Group gap="sm">
        <Loader size="sm" />
        <Text c="dimmed">{t('common.loading')}</Text>
      </Group>
    </Center>
  );
}

/** Rechnungsansicht eines Kontos mit `invoicesOnly`; eigenes Bündel wie die Kontoseite. */
const InvoicesOnlyPage = lazy(() => import('./account-page.js').then(m => ({ default: m.InvoicesOnlyPage })));

/** `/`: zum zuletzt genutzten oder ersten Mandanten weiterleiten; Konten nur mit Rechnungen erst nach nutzbaren. */
export function TenantRedirect() {
  const navigate = useNavigate();
  const me = useMe();
  const memberships = me.data?.memberships;
  const usable = memberships?.filter(m => m.capabilities.view || m.invoicesOnly);
  const target = usable && (usable.find(m => m.tenantId === lastTenant()) ?? usable.find(m => !m.invoicesOnly) ?? usable[0]);

  useEffect(() => {
    if (target) void navigate({ to: target.invoicesOnly ? '/t/$tenantId/account' : '/t/$tenantId', params: { tenantId: target.tenantId }, replace: true });
  }, [navigate, target]);

  if (me.isError) return <ErrorState onRetry={() => void me.refetch()} />;
  if (memberships && usable && usable.length === 0) {
    return (
      <Center mih="60vh" p="md">
        <Stack maw={480}>
          <Alert color="yellow">{memberships.length === 0 ? t('tenants.none') : t('tenants.noneUsable')}</Alert>
          <SignOutButton />
        </Stack>
      </Center>
    );
  }
  return <FullPageLoader />;
}

/** Fehler einer Mandantenabfrage: gesperrt/kein Zugriff ohne Retry, sonst mit Retry. */
export function QueryErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const queryClient = useQueryClient();
  const code = error instanceof ApiRequestError ? error.code : undefined;
  const blocked = code === 'tenant-unavailable' || code === 'forbidden';
  useEffect(() => {
    if (blocked) void queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [blocked, queryClient]);
  if (code === 'tenant-unavailable') return <ErrorState unavailable />;
  if (code === 'unavailable' && error instanceof ApiRequestError && error.detail) {
    return (
      <Alert color="yellow" role="status">
        {error.detail}
      </Alert>
    );
  }
  if (code === 'forbidden') return <ErrorState forbidden />;
  return <ErrorState onRetry={onRetry} />;
}

export function ErrorState({ onRetry, forbidden, unavailable }: { onRetry?: () => void; forbidden?: boolean; unavailable?: boolean }) {
  const title = unavailable ? t('tenants.unavailable') : forbidden ? t('common.forbidden') : t('common.error');
  return (
    <Alert color={unavailable ? 'yellow' : 'red'} role="alert" title={title}>
      {onRetry && (
        <Button size="xs" variant="light" mt="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </Alert>
  );
}

/** Hintergrund der Kopfzeile (app3 `#172b3e`). */
const HEADER_BG = '#172b3e';

function SignOutButton({ light = false }: { light?: boolean }) {
  const { auth } = useServices();
  const navigate = useNavigate();
  return (
    <Button
      variant="subtle"
      size="compact-sm"
      {...(light ? { color: 'gray.0' } : {})}
      onClick={async () => {
        await auth.signOut();
        await navigate({ to: '/login' });
      }}
    >
      {t('nav.logout')}
    </Button>
  );
}

/** Grund, warum ein Konto nicht nutzbar ist (Mandantenwechsel, Kontoseite). */
export function unavailableReason(m: MembershipView): string {
  if (m.accountStatus === 'locked') return t('tenants.locked');
  if (m.accountStatus === 'closed') return t('tenants.closed');
  return t('tenants.notMigrated');
}

/** Zusatz im Mandantenwechsel: Grund bei nicht nutzbaren Konten, bei `invoicesOnly` mit „nur Rechnungen“. */
/** Kontoauswahl wie im Altsystem (Flori 01.11.2026): „ebayname #1229“, ohne E-Mail; ohne eBay-Verbindung „(nicht eingerichtet)“. */
export function tenantLabel(m: MembershipView): string {
  return `${m.ebayUserName ?? t('tenants.ebayMissing')} #${m.tenantId}`;
}

/** Reihenfolge wie im Altsystem: Kontonummer absteigend. */
export function byTenantIdDesc(a: MembershipView, b: MembershipView): number {
  return Number(b.tenantId) - Number(a.tenantId) || b.tenantId.localeCompare(a.tenantId);
}

function switchHint(m: MembershipView): string {
  if (m.invoicesOnly) return ` – ${unavailableReason(m)}, ${t('tenants.invoicesOnly')}`;
  return m.capabilities.view ? '' : ` – ${unavailableReason(m)}`;
}

/** Bereiche, deren Übersicht beim Mandantenwechsel erhalten bleibt (ohne Detailpfad und Filter). */
const SECTION_ROUTES = [
  '/t/$tenantId/items',
  '/t/$tenantId/galleries',
  '/t/$tenantId/articles',
  '/t/$tenantId/designs',
  '/t/$tenantId/media',
  '/t/$tenantId/ebay',
  '/t/$tenantId/account',
] as const;

function TenantSwitch({ memberships, current }: { memberships: MembershipView[]; current: string }) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: s => s.location.pathname });
  if (memberships.length < 2) {
    const only = memberships[0];
    return only ? <Text fw={600}>{tenantLabel(only)}</Text> : null;
  }
  return (
    <Select
      aria-label={t('nav.tenant')}
      w={{ base: 200, sm: 280 }}
      allowDeselect={false}
      searchable={memberships.length > 8}
      value={current}
      data={[...memberships].sort(byTenantIdDesc).map(m => ({
        value: m.tenantId,
        label: `${tenantLabel(m)}${switchHint(m)}`,
        disabled: !m.capabilities.view && !m.invoicesOnly,
      }))}
      onChange={value => {
        if (!value || value === current) return;
        // Gleiche Unterseite beim neuen Mandanten öffnen, Filter verwerfen; Konten nur mit Rechnungen direkt dorthin.
        const rest = memberships.find(m => m.tenantId === value)?.invoicesOnly ? '/account' : pathname.replace(/^\/t\/[^/]+/, '');
        const to = SECTION_ROUTES.find(section => rest.startsWith(section.slice('/t/$tenantId'.length))) ?? '/t/$tenantId';
        void navigate({ to, params: { tenantId: value } });
      }}
    />
  );
}

export function TenantLayout({ tenantId }: { tenantId: string }) {
  const me = useMe();
  const navigate = useNavigate();
  const membership = me.data?.memberships.find(m => m.tenantId === tenantId);
  const invoicesOnly = membership?.invoicesOnly === true;
  const section = useRouterState({ select: s => /^\/t\/[^/]+\/([a-z]+)/.exec(s.location.pathname)?.[1] ?? 'start' });
  const atAccount = useRouterState({ select: s => /^\/t\/[^/]+\/account$/.test(s.location.pathname) });
  const pathname = useRouterState({ select: s => s.location.pathname });
  const [navOpen, { toggle: toggleNav, close: closeNav }] = useDisclosure(false);

  // Nach dem Wechsel der Seite das Menü auf schmalen Bildschirmen wieder schließen.
  useEffect(() => closeNav(), [pathname, closeNav]);

  useEffect(() => {
    if (membership) rememberTenant(membership.tenantId);
  }, [membership]);

  // Nur Rechnungen: jede andere Unterseite auf die Rechnungsansicht; deren Seiten werden nie gerendert (kein Abruf).
  useEffect(() => {
    if (invoicesOnly && !atAccount) void navigate({ to: '/t/$tenantId/account', params: { tenantId }, replace: true });
  }, [invoicesOnly, atAccount, navigate, tenantId]);

  return (
    <AppShell
      header={{ height: 60 }}
      {...(invoicesOnly ? {} : { navbar: { width: 210, breakpoint: 'sm', collapsed: { mobile: !navOpen } } })}
      padding={{ base: 'sm', sm: 'lg' }}
    >
      {/* Kopfzeile im Marineblau des bisherigen PAT (app3), sonst schlicht (Flori 01.11.2026: „nur ein Hauch“). */}
      <AppShell.Header bg={HEADER_BG} c="white" withBorder={false}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="md" wrap="nowrap">
            {/* Schmale Bildschirme: Navigation hinter dem Menüknopf statt über dem ganzen Inhalt. */}
            {!invoicesOnly && <Burger opened={navOpen} onClick={toggleNav} hiddenFrom="sm" size="sm" color="white" aria-label={t('nav.menu')} />}
            <Text fw={700} c="white" visibleFrom="xs">
              {t('app.name')}
            </Text>
          </Group>
          {/* Konto rechts neben „Abmelden“ (Flori 01.11.2026). */}
          <Group gap="sm" wrap="nowrap">
            {membership && (
              <Badge variant="white" color="dark" visibleFrom="md">
                {t(`tenants.role.${membership.role}`)}
              </Badge>
            )}
            {me.data && <TenantSwitch memberships={me.data.memberships} current={tenantId} />}
            <SignOutButton light />
          </Group>
        </Group>
      </AppShell.Header>
      {!invoicesOnly && (
        <AppShell.Navbar p="xs">
          {/* Nur auf der Übersicht selbst aktiv; sonst markiert der Router den Präfix-Link auf jeder Unterseite mit. */}
          <RouterNavLink to="/t/$tenantId" params={{ tenantId }} label={t('nav.start')} active={section === 'start'} activeOptions={{ exact: true }} />
          {/* Reihenfolge Flori 01.11.2026; Angebote und Live-Update auch in der BETA, nur lesend (Flori 31.10.2026, BETA_PLAN.md §4). */}
          <RouterNavLink to="/t/$tenantId/designs" params={{ tenantId }} label={t('nav.designs')} active={section === 'designs'} />
          <RouterNavLink to="/t/$tenantId/articles" params={{ tenantId }} label={t('nav.articles')} active={section === 'articles'} />
          <RouterNavLink to="/t/$tenantId/media" params={{ tenantId }} label={t('nav.media')} active={section === 'media'} />
          {membership?.features.crossSelling && (
            <RouterNavLink to="/t/$tenantId/galleries" params={{ tenantId }} label={t('nav.galleries')} active={section === 'galleries'} />
          )}
          {/* eBay-Verbindung und Live-Update auf einer Seite (Flori 01.11.2026). */}
          <RouterNavLink to="/t/$tenantId/ebay" params={{ tenantId }} label={t('nav.ebay')} active={section === 'ebay'} />
          <RouterNavLink to="/t/$tenantId/items" params={{ tenantId }} label={t('nav.items')} active={section === 'items'} />
          <RouterNavLink to="/t/$tenantId/account" params={{ tenantId }} label={t('nav.account')} active={section === 'account'} />
        </AppShell.Navbar>
      )}
      <AppShell.Main>
        {me.isPending && <FullPageLoader />}
        {me.isError && <ErrorState onRetry={() => void me.refetch()} />}
        {me.data && !membership && <ErrorState forbidden />}
        {membership && invoicesOnly && (
          <Suspense fallback={<FullPageLoader />}>
            <InvoicesOnlyPage key={tenantId} membership={membership} />
          </Suspense>
        )}
        {membership && !invoicesOnly && !membership.capabilities.view && <ErrorState unavailable />}
        {/* Keine BETA-Hinweise mehr (Flori 01.11.2026); was noch fehlt, steht auf der Übersicht. */}
        {membership?.capabilities.view && !invoicesOnly && <Outlet />}
      </AppShell.Main>
    </AppShell>
  );
}

